import React, { useCallback } from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import PortfolioItemCard from "../PortfolioItemCard";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface PortfolioItemsListProps {
  items: PortfolioItem[];
  onRemove: (index: number) => void;
  onEdit: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  isProcessing?: boolean;
  onReorder?: (items: PortfolioItem[]) => void;
  onSetFeatured?: (id: string) => void;
  onUpdateStatus?: (id: string, status: "draft" | "published") => void;
}

// Memoize the card for performance
const MemoPortfolioItemCard = React.memo(PortfolioItemCard);

// Memoize the sortable wrapper for performance
const SortablePortfolioItemCard = React.memo(({
  item,
  index,
  onRemove,
  onEdit,
  onRename,
  isProcessing,
  onSetFeatured,
  onUpdateStatus,
}: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    animateLayoutChanges: () => true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    height: "100%",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`h-full${isDragging ? " isDragging" : ""}`}
    >
      <MemoPortfolioItemCard
        item={item}
        index={index}
        onRemove={onRemove}
        onEdit={onEdit}
        onRename={onRename}
        isProcessing={isProcessing}
        onSetFeatured={onSetFeatured}
        onUpdateStatus={onUpdateStatus}
      />
    </div>
  );
});

const PortfolioItemsList = ({
  items,
  onRemove,
  onEdit,
  onRename,
  isProcessing = false,
  onReorder,
  onSetFeatured,
  onUpdateStatus,
}: PortfolioItemsListProps) => {
  // Memoize handlers to avoid unnecessary re-renders
  const handleRemove = useCallback(onRemove, [onRemove]);
  const handleEdit = useCallback(onEdit, [onEdit]);
  const handleRename = useCallback(onRename, [onRename]);
  const handleSetFeatured = useCallback(onSetFeatured, [onSetFeatured]);
  const handleUpdateStatus = useCallback(onUpdateStatus, [onUpdateStatus]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Only update state if the order actually changes
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item: any) => item.id === active.id);
      const newIndex = items.findIndex((item: any) => item.id === over.id);
      if (oldIndex !== newIndex) {
        if (newIndex === 0) {
          const draggedItem = items[oldIndex];
          if (draggedItem.status === "draft") {
            handleUpdateStatus?.(String(active.id), "published");
          }
          handleSetFeatured?.(String(active.id));
        } else {
          let reorderedItems = arrayMove(items, oldIndex, newIndex);
          if (items[oldIndex]?.is_featured) {
            reorderedItems = reorderedItems.map((item: any) =>
              item.id === active.id ? { ...item, is_featured: false } : item
            );
          }
          onReorder?.(reorderedItems);
        }
      }
    }
  }, [items, handleSetFeatured, handleUpdateStatus, onReorder]);

  if (!items?.length) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((item: any) => item.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item: any, index: number) => (
            <SortablePortfolioItemCard
              key={item.id}
              item={item}
              index={index}
              onRemove={handleRemove}
              onEdit={handleEdit}
              onRename={handleRename}
              isProcessing={isProcessing}
              onSetFeatured={handleSetFeatured}
              onUpdateStatus={handleUpdateStatus}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default PortfolioItemsList;
